import React from "react";
import { Flame, Snowflake, ExternalLink } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const HOT_LAM =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/qmx2m91b_Hot%20Laminator.jpg";
const COLD_LAM =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/haypgpv0_Cold%20Lam-%201.jpg";

const LAMINATORS = [
  {
    icon: Flame,
    eyebrow: "Heat-activated",
    title: "Hot Laminators",
    body:
      "Roll-to-roll heat lamination for posters, signage, and high-traffic displays. Built for daily classroom use and full-volume print shops.",
    bullets: [
      "Adjustable temperature control",
      "Top + bottom roll loading",
      "Ideal for full-color posters & banners",
    ],
    img: HOT_LAM,
    accent: "from-orange-500/20 via-transparent to-transparent",
  },
  {
    icon: Snowflake,
    eyebrow: "No-heat",
    title: "Cold Laminators",
    body:
      "Pressure-sensitive cold lamination — safe for heat-sensitive media, photos, and student artwork. Quiet, fast, and energy-efficient.",
    bullets: [
      "No warm-up time, ready instantly",
      "Safe for ink-jet & photo prints",
      "Hand-crank operation, no power required",
    ],
    img: COLD_LAM,
    accent: "from-sky-400/20 via-transparent to-transparent",
  },
];

export default function LaminatorShowcase() {
  const { openQuote } = useQuote();

  return (
    <section
      data-testid="laminator-showcase-section"
      className="bg-[#F4F6F4] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
            Laminator lineup
          </div>
          <h2
            data-testid="laminator-showcase-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
          >
            Hot or Cold —{" "}
            <span className="text-teal">We've Got You Covered.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Two professional laminator types, one trusted source. Match the
            right finishing tool to your media, your budget, and your school's
            workflow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {LAMINATORS.map((lam, i) => {
            const Icon = lam.icon;
            return (
              <div
                key={lam.title}
                data-testid={`laminator-card-${i}`}
                className="group relative bg-white border border-slate-200 hover:border-teal/40 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col"
              >
                <div className="cmyk-ribbon h-1 w-full" aria-hidden />
                <div className="relative aspect-[16/10] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8 overflow-hidden">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${lam.accent} pointer-events-none`}
                    aria-hidden
                  />
                  <img
                    src={lam.img}
                    alt={`${lam.title} — professional ${lam.eyebrow.toLowerCase()} laminator`}
                    loading="lazy"
                decoding="async"
                    className="relative max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center group-hover:bg-teal transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={2} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal">
                      {lam.eyebrow}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-navy mb-3 leading-tight">
                    {lam.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6">
                    {lam.body}
                  </p>
                  <ul className="space-y-2 mt-auto pt-5 border-t border-slate-200">
                    {lam.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-sm text-slate-700"
                      >
                        <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-teal" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-slate-700 text-base sm:text-lg max-w-2xl leading-relaxed">
            Not sure which laminator fits your school?{" "}
            <strong className="text-navy">
              We'll match it to your print volume and media types.
            </strong>
          </p>
          <button
            data-testid="laminator-showcase-quote-btn"
            onClick={openQuote}
            title="Opens in a new tab"
            className="group inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#091638] text-white font-bold px-7 py-3.5 rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Request a Quote
            <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
